import React from 'react';
import styled from 'styled-components';

import {
  CustomFieldWithValue,
  errorFieldsTypes,
  ErrorsFields,
  FieldsHelpText,
  MultiOptionValue,
  prefixTypes,
  RequiredFields,
} from '@/core/interfaces/common';
import { FormInput } from '@/core/components/FormInput';
import { FormSelect } from '@/core/components/FormSelect';
import { CustomFormInput } from '@/core/components/CustomFormInput';
import { AutoAddressInput } from '@/core/components/AutoAddressInput';

import {
  fieldTypes,
  prefixes,
} from '@/utils/constants';
import mediaQueries from '@/utils/mediaQueries';

const {
  small,
} = mediaQueries;

const CustomContactFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 32px;

  // Somewhat arbitrarily chosen
  @media (min-width: 624px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 24px;
  }

  // Breakpoint for new form going UNDER the description
  @media (min-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 16px 24px;
  }

  // Breakpoint for two-column page layout (form beside description)
  @media (min-width: 1024px) {
    grid-template-columns: 1fr;
  }

  // Form column is wide enough for two fields per row
  @media (min-width: 1280px) {
    grid-template-columns: 1fr 1fr;
  }
`;

export interface CustomFieldErrors {
  value: Array<string>;
}

export interface ContactFieldsProps {
  backgroundColor: string;
  error: ErrorsFields;
  email: string;
  firstName: string;
  lastName: string;
  streetAddress: string;
  phoneNumber: string | number;
  prefix: prefixTypes;
  zipCode: string;
  customFields: Array<CustomFieldWithValue>;
  handleBlurAddress?: (streetAddress?: string, zipCode?: string) => void;
  handleChangeAddress: (value: string) => void;
  handleClearCustomFieldError: (value: number) => void;
  handleClearFieldError: (value: string) => void;
  handleCustomFieldChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    index: number
  ) => void;
  handleMultiCustomFieldChange: (
    values: Array<MultiOptionValue>,
    index: number,
  ) => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handlePhoneInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleSetCustomFieldError: (index: number, message: string) => void;
  handleSetFieldError: (field: errorFieldsTypes, message: string) => void;
  handleSetZipCode: (value: string) => void;
  renderContactError: (value: errorFieldsTypes) => string;
  requiredFields?: RequiredFields;
  helpText?: FieldsHelpText;
  ariaLabels?: {
    prefix?: string;
    firstName?: string;
    lastName?: string;
    streetAddress?: string;
    zipCode?: string;
    email?: string;
    phoneNumber?: string;
    customFields?: { [uuid: string]: string };
  };
}

export const FormInputWrapper = styled.div`
  min-width: 0;
  padding-top: 0;
`;

export const CustomContactFields = ({
  backgroundColor,
  error,
  email,
  handleBlurAddress,
  handleChangeAddress,
  handleCustomFieldChange,
  handleClearCustomFieldError,
  handleClearFieldError,
  handleMultiCustomFieldChange,
  handleInputChange,
  handlePhoneInputChange,
  handleSelectChange,
  handleSetCustomFieldError,
  handleSetFieldError,
  handleSetZipCode,
  prefix,
  phoneNumber,
  requiredFields,
  firstName,
  lastName,
  renderContactError,
  streetAddress,
  customFields,
  zipCode,
  ariaLabels,
  helpText,
}: ContactFieldsProps) => (
  <CustomContactFieldsWrapper
    role="group"
    aria-label="Contact fields"
  >
    <FormInputWrapper className="muster-field-prefix">
      <FormSelect
        backgroundcolor={backgroundColor}
        error={() => renderContactError('prefix')}
        helpText={helpText?.prefix}
        name="Prefix"
        onChange={handleSelectChange}
        onFocus={() => handleClearFieldError('prefix')}
        onInvalidField={() => handleSetFieldError('prefix', 'Please select a prefix option')}
        options={prefixes}
        isRequired={!!requiredFields?.prefix}
        value={prefix}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-firstname">
      <FormInput
        ariaLabel={ariaLabels?.firstName}
        autocomplete="given-name"
        backgroundcolor={backgroundColor}
        error={() => renderContactError('firstName')}
        helpText={helpText?.firstName}
        label="First Name"
        name="firstName"
        onChange={handleInputChange}
        onFocus={() => handleClearFieldError('firstName')}
        onInvalidField={() => handleSetFieldError('firstName', 'Please enter your first name')}
        isRequired={!!requiredFields?.firstName}
        value={firstName}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-lastname">
      <FormInput
        ariaLabel={ariaLabels?.lastName}
        autocomplete="family-name"
        backgroundcolor={backgroundColor}
        error={() => renderContactError('lastName')}
        helpText={helpText?.lastName}
        isRequired={!!requiredFields?.lastName}
        label="Last Name"
        name="lastName"
        onChange={handleInputChange}
        onFocus={() => handleClearFieldError('lastName')}
        onInvalidField={() => handleSetFieldError('lastName', 'Please enter your last name')}
        value={lastName}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-address">
      <AutoAddressInput
        ariaLabel={ariaLabels?.streetAddress}
        backgroundcolor={backgroundColor}
        error={() => renderContactError('streetAddress')}
        helpText={helpText?.streetAddress}
        isRequired={!!requiredFields?.streetAddress}
        onBlur={handleBlurAddress}
        onChangeAddress={handleChangeAddress}
        onFocus={() => handleClearFieldError('streetAddress')}
        onInvalidField={() => handleSetFieldError('streetAddress', 'Please enter your street address')}
        setZipCode={handleSetZipCode}
        value={streetAddress}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-zipcode">
      <FormInput
        ariaLabel={ariaLabels?.zipCode}
        autocomplete="postal-code"
        backgroundcolor={backgroundColor}
        error={() => renderContactError('zipCode')}
        helpText={helpText?.zipCode}
        isRequired={!!requiredFields?.zipCode}
        label="Zip Code"
        maxlength={5}
        name="zipCode"
        onBlur={handleBlurAddress}
        onChange={handleInputChange}
        onFocus={() => handleClearFieldError('zipCode')}
        onInvalidField={() => handleSetFieldError('zipCode', 'Please enter your zip code')}
        type="text"
        value={zipCode}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-email">
      <FormInput
        ariaLabel={ariaLabels?.email}
        autocomplete="email"
        backgroundcolor={backgroundColor}
        error={() => renderContactError('email')}
        helpText={helpText?.email}
        isRequired={!!requiredFields?.email}
        label="Email"
        name="email"
        onChange={handleInputChange}
        onFocus={() => handleClearFieldError('email')}
        onInvalidField={() => handleSetFieldError('email', 'Please enter your email')}
        type="email"
        value={email}
      />
    </FormInputWrapper>
    <FormInputWrapper className="muster-field-phone">
      <FormInput
        ariaLabel={ariaLabels?.phoneNumber}
        autocomplete="tel"
        backgroundcolor={backgroundColor}
        error={() => renderContactError('phoneNumber')}
        helpText={helpText?.phoneNumber}
        isRequired={!!requiredFields?.phoneNumber}
        label="Phone number"
        name="phoneNumber"
        onChange={handlePhoneInputChange}
        onFocus={() => handleClearFieldError('phoneNumber')}
        onInvalidField={() => handleSetFieldError('phoneNumber', 'Please enter your phone number')}
        type="text"
        value={phoneNumber}
      />
    </FormInputWrapper>
    {customFields?.map((customFieldData, index) => (
      <FormInputWrapper
        className="muster-field-custom"
        key={customFieldData.uuid}
      >
        <CustomFormInput
          ariaLabel={ariaLabels?.customFields?.[customFieldData.uuid]}
          backgroundcolor={backgroundColor}
          customFieldData={customFieldData}
          error={() => error?.customFields?.[index]?.value?.[0] || false}
          helpText={customFieldData.description}
          isRequired={!!requiredFields?.custom}
          onChange={event => handleCustomFieldChange(event, index)}
          onMultiChange={values => handleMultiCustomFieldChange(values, index)}
          onFocus={() => handleClearCustomFieldError(index)}
          onInvalidField={() => {
            let message: string;

            if (customFieldData.fieldType === fieldTypes.textField) {
              message = 'Please enter a response';
            } else if (customFieldData.fieldType === fieldTypes.multipleChoiceField) {
              message = 'Select at least one option';
            } else {
              message = 'Please select an option';
            }

            handleSetCustomFieldError(index, message);
          }}
        />
      </FormInputWrapper>
    ))}
  </CustomContactFieldsWrapper>
);

CustomContactFields.defaultProps = {
  handleBlurAddress: () => null,
};
